import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Alert,
} from "@mui/material";
import axiosClient from "../axiosClient";
import { useStateContext } from "../context/ContextProvider";
import { useTranslation } from "react-i18next";

const Staff = () => {
  const [errors, setErrors] = useState({ client_id: "", staff_id: "" });
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [message, setMessage] = useState({ text: "", severity: "success" });
  const { token } = useStateContext();
  const { t } = useTranslation();

  useEffect(() => {
    Promise.all([
      axiosClient.get("api/staff-users/"),
      axiosClient.get("api/client-users/"),
    ])
      .then(([staffRes, clientRes]) => {
        setStaffUsers(staffRes?.data || []);
        setClientUsers(clientRes?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setMessage({
          text: t("Failed to load users. Please try again."),
          severity: "error",
        });
      });
  }, [t]);

  useEffect(() => {
    if (message.text && message.severity === "success") {
      const timer = setTimeout(() => {
        setMessage({ text: "", severity: "success" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const validateForm = () => {
    const newErrors = { client_id: "", staff_id: "" };
    let isValid = true;

    if (!selectedClientId) {
      newErrors.client_id = t("Patient is required");
      isValid = false;
    }
    if (!selectedStaffId) {
      newErrors.staff_id = t("Doctor is required");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      client_id: selectedClientId,
      staff_id: selectedStaffId,
    };

    axiosClient
      .post("api/assign_client_to_staff/", payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `JWT ${token}`,
        },
      })
      .then(() => {
        setMessage({
          text: t("Client assigned to staff successfully"),
          severity: "success",
        });
        setSelectedStaffId("");
        setSelectedClientId("");
        setErrors({ client_id: "", staff_id: "" });
      })
      .catch((err) => {
        console.error("Assignment error:", err);
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.non_field_errors?.[0] ||
          t("Failed to assign client. Please try again.");
        setMessage({ text: errorMessage, severity: "error" });
      });
  };

  const handleClear = () => {
    setSelectedClientId("");
    setSelectedStaffId("");
    setErrors({ client_id: "", staff_id: "" });
    setMessage({ text: "", severity: "success" });
  };

  return (
    <Card elevation={16}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t("Clients to Staff")}
        </Typography>
        {message.text && (
          <Alert severity={message.severity} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Box sx={{ "& .MuiFormControl-root": { marginBottom: "1rem" } }}>
            <FormControl fullWidth variant="outlined" error={!!errors.staff_id}>
              <InputLabel id="staff-select-label">{t("Select Staff")}</InputLabel>
              <Select
                labelId="staff-select-label"
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
                label={t("Select Staff")}
                sx={{
                  textAlign: "left",
                  ".MuiSelect-select": {
                    display: "flex",
                    justifyContent: "flex-start",
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t("None")}</em>
                </MenuItem>
                {staffUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {`${user.custom_user.first_name} ${user.custom_user.last_name}`}
                  </MenuItem>
                ))}
              </Select>
              {errors.staff_id && (
                <FormHelperText>{errors.staff_id}</FormHelperText>
              )}
            </FormControl>
            <FormControl fullWidth variant="outlined" error={!!errors.client_id}>
              <InputLabel id="client-select-label">{t("Select Client")}</InputLabel>
              <Select
                labelId="client-select-label"
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                label={t("Select Client")}
                sx={{
                  textAlign: "left",
                  ".MuiSelect-select": {
                    display: "flex",
                    justifyContent: "flex-start",
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t("None")}</em>
                </MenuItem>
                {clientUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {`${user.custom_user.first_name} ${user.custom_user.last_name}`}
                  </MenuItem>
                ))}
              </Select>
              {errors.client_id && (
                <FormHelperText>{errors.client_id}</FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button type="submit" variant="contained" color="primary">
              {t("Submit")}
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleClear}>
              {t("Clear")}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default Staff;